import React from 'react';
import { Helmet } from 'react-helmet';

import { PDFBoldStatement } from './components/PDFBoldStatement';
import { PDFWhySwitch } from './components/PDFWhySwitch';
import { PDFQuestions } from './components/PDFQuestions';
import { PDFTestimonial } from './components/PDFTestimonial';
import { PDFTLDR } from './components/PDFTLDR';
// const CTACard = lazy(() => import('../Landing/components/CTACard'));
import CTACard from '../Landing/components/CTACard';
type Props = {
  className?: string;
};

const PDFS: React.FC<Props> = ({ className }) => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Why PDF Menus Suck | TableTab</title>
        <meta name="robots" content="index, follow" />
        <meta
          name="description"
          content="TableTab is a mobile order and payment platform that helps restaurants operate the most personalized, safe, efficient, &amp; profitable dining experience available today. See why restaurants using PDF menus made the switch to a free digital menu on TableTab."
        />
        <link rel="canonical" href="https://tabletab.io/compare/pdfs/" />
      </Helmet>
      <PDFBoldStatement className={'lg:mt-20 mt-10'} />
      <PDFWhySwitch className={'lg:mt-32 mt-10 lg:mx-10 xl:mx-20 2xl:mx-32'} />
      <PDFQuestions
        image_placement={'right'}
        question={
          'Wait, can TableTab really replace PDF menus or menus on my website?'
        }
        answer={
          <span>
            Putting a menu on a PDF is like{' '}
            <a
              className={'text-primary-600 hover:text-primary-700'}
              href="https://media.giphy.com/media/jUuYhnyTrzeX61Nvvb/giphy.gif"
              target={'_'}
            >
              serving wine in a mug
            </a>
            . It damages the quality of your menu, literally. We built TableTab
            to do the opposite - to heighten the quality of your menu and
            elevate the guest experience by doing so. We're the glass you pour
            your best wine into.
          </span>
        }
        image_url={''}
        svg_bg_color={'bg-blue-200'}
        svg_path_color={'text-blue-800'}
        className={'xl:mx-20 lg:mx-10 2xl:mx-32 md:mt-32 mt-20'}
        svg_path={'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4'}
      />
      <PDFQuestions
        image_placement={'left'}
        question={'Is a digital menu really better for the guest?'}
        answer={
          <span>
            Your PDF is already digital, just hosted in a document. That's what
            makes it especially difficult to use. Our menus are freed from being
            constrained in a document, giving them{' '}
            <a
              className={'text-primary-600 hover:text-primary-700'}
              href="/product#digital-menus"
            >
              {' '}
              special powers
            </a>{' '}
            like beautiful visuals, easy navigation, and real-time updates. It's
            the best kind of menu a guest can have.
          </span>
        }
        image_url={''}
        svg_bg_color={'bg-green-200'}
        svg_path_color={'text-green-800'}
        className={'xl:mx-20 lg:mx-10 2xl:mx-32 md:mt-32 mt-20'}
        svg_path={'M13 10V3L4 14h7v7l9-11h-7z'}
      />
      <PDFTestimonial className={'mt-20 lg:mt-32'} />

      <PDFQuestions
        image_placement={'right'}
        question={'Isn’t having a mobile-friendly menu expensive?'}
        answer={
          <span>
            Not with us. You can set up your mobile-friendly menu without paying
            a penny. Really.
          </span>
          // 'Our menus areWe’ve waived all of our setup fees—over $500 in value— to get you back on your feet. We’ll handle your menu so you can keep doing what you do best—serving up guest experiences.'
        }
        image_url={''}
        className={'xl:mx-20 lg:mx-10 2xl:mx-32 mt-32 md:mt-32'}
        svg_bg_color={'bg-primary-200'}
        svg_path_color={'text-primary-800'}
        svg_path={
          'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z'
        }
      />
      <PDFQuestions
        image_placement={'left'}
        svg_bg_color={'bg-yellow-200'}
        svg_path_color={'text-yellow-800'}
        question={
          'But our operation prides itself on service and hospitality... won’t contactless ordering detract from that?'
        }
        answer={
          <span>
            It may sound contradictory, but our partners will tell you their
            service improved through us. How? Freeing up staff from handling
            order and payment puts focus entirely on{' '}
            <a
              className={'text-primary-600 hover:text-primary-700'}
              href="/use-cases/fsr#margin-impact"
            >
              {' '}
              great service
            </a>{' '}
            (often resulting in higher tips), while{' '}
            <a
              className={'text-primary-600 hover:text-primary-700'}
              href="/use-cases/fsr#margin-impact"
            >
              {' '}
              keeping overhead low
            </a>{' '}
            in the process.
          </span>
        }
        image_url={''}
        className={'xl:mx-20 lg:mx-10 2xl:mx-32 md:mt-32 mt-20'}
        svg_path={
          'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
        }
      />
      <PDFQuestions
        image_placement={'right'}
        question={
          'I already have a online ordering platform for delivery and takeout.... can’t I just use that for dine-in?'
        }
        answer={
          <span>
            We’d caution against it. Unlike takeout and delivery platforms, we
            built TableTab with{' '}
            <a
              className={'text-primary-600 hover:text-primary-700'}
              href="/use-cases/fsr#capabilities"
            >
              {' '}
              dine-in operations and incremental spend in mind
            </a>
            . Guests order limitless times instantly under a{' '}
            <a
              className={'text-primary-600 hover:text-primary-700'}
              href="/product#order-pay"
            >
              {' '}
              digital tab
            </a>{' '}
            that charges their card at the end of the meal. It’s a better
            experience for your guest and a more profitable one for you.
          </span>
        }
        image_url={''}
        svg_bg_color={'bg-purple-200'}
        svg_path_color={'text-purple-800'}
        className={'xl:mx-20 lg:mx-10 2xl:mx-32 md:mt-32 mt-20'}
        svg_path={'M5 11l7-7 7 7M5 19l7-7 7 7'}
      />

      <PDFQuestions
        image_placement={'left'}
        question={
          'I’m worried if people use mobile payment, they may use fraudulent credit cards or simply walkout.'
        }
        answer={
          <span>
            Our payment partner, Stripe, offers the{' '}
            <a
              className={'text-primary-600 hover:text-primary-700'}
              href="https://stripe.com/security"
              target={'_'}
            >
              {' '}
              highest level fraud detection and payment security
            </a>
            . Additionally, we place a hold on a guest's card when they begin an
            order and automatically charge it when they finish their meal. With
            TableTab, walkouts are a thing of the past!
          </span>
        }
        image_url={''}
        svg_bg_color={'bg-blue-200'}
        svg_path_color={'text-blue-800'}
        className={'xl:mx-20 lg:mx-10 2xl:mx-32 md:mt-32 mt-20'}
        svg_path={
          'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
        }
      />
      <PDFTLDR className={'mt-10 md:mt-32 lg:mt-40 2xl:mt-48 py-10 md:py-20'} />
      <div className="mt-5  w-1/10  text-center" id={'pdf_banner_cta'}>
        <a
          href="https://www.figma.com/figma-vs-sketch/"
          target={'_'}
          className="items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-full text-gray-800  hover:text-gray-800"
        >
          Design inspiration from Figma
        </a>
      </div>
      <CTACard className={'md:pt-20 pt-10 md:mt-20'} />
    </>
  );
};

export default PDFS;
